import { createBrowserHistory } from 'history';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// Create a history object to use for programmatic navigation
const history = createBrowserHistory();
let backButtonListenerAdded = false;

// Keep track of navigation history length to determine if we can go back
let historyLength = window.history.length;

/**
 * Initialize back button handler for Android
 * Prevents app from exiting when back button is pressed if there are screens to go back to
 */
export const initBackButtonHandler = (): void => {
  // Only register once and only on Android native platform
  if (backButtonListenerAdded || !Capacitor.isNativePlatform()) {
    return;
  }

  console.log('NavigationService: Initializing hardware back button handler');
  
  App.addListener('backButton', ({ canGoBack }) => {
    console.log('NavigationService: Hardware back button pressed, canGoBack:', canGoBack);
    
    // Check if we can go back in history
    if (canGoBack && window.history.length > 1) {
      console.log('NavigationService: Going back in history');
      history.back();
    } else {
      // If we're at the root of our history stack, let the app handle it 
      // (this would normally exit the app on Android)
      console.log('NavigationService: At root of history stack');
      if (window.location.pathname !== '/home') {
        // If not at home, navigate to home as a last resort
        console.log('NavigationService: Not at home, navigating to home');
        navigate('/home');
        return;
      }
      
      // No more history, exit app
      console.log('NavigationService: Exiting app');
      App.exitApp();
    }
  });
  
  // Listen for history changes to update our counter
  window.addEventListener('popstate', () => {
    historyLength = window.history.length;
  });
  
  backButtonListenerAdded = true;
};

/**
 * Navigate to a specific path with optional query parameters
 * @param path The path to navigate to
 * @param params Optional query parameters
 */
export const navigate = (path: string, params?: Record<string, string>): void => {
  // Build query string from params if they exist
  let queryString = '';
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, value);
    });
    queryString = `?${searchParams.toString()}`;
  }

  // Navigate to the desired path with query parameters
  console.log(`NavigationService: Navigating to ${path}${queryString}`);
  history.push(`${path}${queryString}`);
  historyLength = window.history.length;
};

/**
 * Go back to the previous screen
 * @returns true if successfully went back, false if at the root
 */
export const goBack = (): boolean => {
  if (window.history.length > 1) {
    console.log('NavigationService: Going back in history');
    history.back();
    return true;
  }
  console.log('NavigationService: Cannot go back, at root of history');
  return false;
};

/**
 * Get the current location object
 */
export const getCurrentLocation = () => {
  return history.location;
};

export default {
  navigate,
  getCurrentLocation,
  goBack,
  initBackButtonHandler,
  history
};
